import { type ReactNode, createContext, useContext, useState } from "react"

type FavoriteKey = `${string}:${number}`

interface FavoritesContextType {
  isFavorited: (type: string, id: number) => boolean | undefined
  setFavorite: (type: string, id: number, value: boolean) => void
  getFavoritesCount: (type: string, id: number) => number | undefined
  setFavoritesCount: (type: string, id: number, count: number) => void
}

const FavoritesContext = createContext<FavoritesContextType | null>(null)

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<Map<FavoriteKey, boolean>>(
    new Map(),
  )
  const [counts, setCounts] = useState<Map<FavoriteKey, number>>(new Map())

  const makeKey = (type: string, id: number): FavoriteKey => `${type}:${id}`

  const isFavorited = (type: string, id: number) =>
    favorites.get(makeKey(type, id))
  const setFavorite = (type: string, id: number, value: boolean) => {
    setFavorites((prev) => new Map(prev).set(makeKey(type, id), value))
  }
  const getFavoritesCount = (type: string, id: number) =>
    counts.get(makeKey(type, id))
  const setFavoritesCount = (type: string, id: number, count: number) => {
    setCounts((prev) => new Map(prev).set(makeKey(type, id), count))
  }

  return (
    <FavoritesContext.Provider
      value={{ isFavorited, setFavorite, getFavoritesCount, setFavoritesCount }}
    >
      {children}
    </FavoritesContext.Provider>
  )
}

export function useFavorites() {
  const context = useContext(FavoritesContext)
  if (!context)
    throw new Error("useFavorites must be used within FavoritesProvider")
  return context
}
